// © Microsoft Corporation. All rights reserved.

import { findIndex } from "lodash-es";
import { getUserInfoAsync, getDeviceInfoAsync, getLocationInfoAsync, logErrorAsync } from "./SapphireBridge";
import { UserConfig, UserInformation, DeviceInformation, DevicePlatform } from "./SapphireTypes";

/** The variable to hold user information. */
let userInformation: UserInformation;

/** The variable to hold device information. */
let deviceInformation: DeviceInformation;

/** The variable to hold location information. */
let locationInformation: any;

/** The variable to hold user configuration. */
let userConfig: UserConfig;

/** Cache URI to paramsObj mapping. */
const hrefParamsObj: any = {};

/**
 * Makes the Sapphire API calls to get the user auth information.
 * @param configOverrides - The config overrides
 */
export async function initUserConfig(configOverrides: any): Promise<void> {
    const locationHref = window.location.href;
    const sapphireId = getQueryParameterByName("muid", locationHref);

    if (sapphireId) {
        return initUserConfigForSuperAppAndroid();
    } else {
        return initUserConfigForOthers(configOverrides);
    }
}

/**
 * Makes the Sapphire API calls to get the user auth information for iOS SA, and Launcher.
 * @param configOverrides - The config overrides
 */
async function initUserConfigForOthers(configOverrides: any): Promise<void> {
    userConfig = {};

    window.performance.mark("HP-initUserConfigForOthers-DeviceInformation-Start");
    deviceInformation = await getDeviceInformation();
    window.performance.mark("HP-initUserConfigForOthers-DeviceInformation-End");
    window.performance.measure("HP-initUserConfigForOthers-DeviceInformation", "HP-initUserConfigForOthers-DeviceInformation-Start", "HP-initUserConfigForOthers-DeviceInformation-End");

    if (deviceInformation) {
        userConfig.muid = `m-${deviceInformation.sapphireId}`;
        userConfig.sapphireId = deviceInformation.sapphireId;
        userConfig.anid = deviceInformation.ANID;
        userConfig.adid = deviceInformation.ADID;
        userConfig.market = getValidMarket(deviceInformation.market);
        userConfig.features = deviceInformation.expFeatures;
        userConfig.activityId = deviceInformation.expActivityId;
        userConfig.isDarkMode = deviceInformation.isDarkMode;
        userConfig.platform = deviceInformation.platform;
        userConfig.bridgeVersionInt = deviceInformation.bridgeVersionInt;
        userConfig.fontSize = deviceInformation.fontSize;
        userConfig.isChinaBuild = deviceInformation.isChinaBuild;

        // isEos property is only available in Launcher.
        // true - Means it is Zeta launcher
        // false - Means it is in market launcher
        userConfig.isEos = deviceInformation.isEos !== undefined ? deviceInformation.isEos : false;

        setMarketInfo(userConfig);
    }

    window.performance.mark("HP-initUserConfigForOthers-UserInformation-Start");
    await getUserInformationInternal();
    window.performance.mark("HP-initUserConfigForOthers-UserInformation-End");
    window.performance.measure("HP-initUserConfigForOthers-UserInformation", "HP-initUserConfigForOthers-UserInformation-Start", "HP-initUserConfigForOthers-UserInformation-End");

    if (userInformation) {
        userConfig.accountType = userInformation.accountType;
        userConfig.isSignedIn = userInformation.isSignedIn;
    }

    window.performance.mark("HP-initUserConfigForOthers-LocationInformation-Start");
    await getLocationInformationInternal();
    window.performance.mark("HP-initUserConfigForOthers-LocationInformation-End");
    window.performance.measure("HP-initUserConfigForOthers-LocationInformation", "HP-initUserConfigForOthers-LocationInformation-Start", "HP-initUserConfigForOthers-LocationInformation-End");

    if (locationInformation) {
        userConfig.latitude = locationInformation.lat;
        userConfig.longitude = locationInformation.lng;
    }

    const urlParams = new URLSearchParams(window.location.search);
    userConfig.isPageRefresh = urlParams && urlParams.get("isPageRefresh") ? urlParams.get("isPageRefresh") === "1" : false;

    if (configOverrides) {
        userConfig.muid = configOverrides.muid;
        userConfig.activityId = configOverrides.activityId;
        userConfig.features = configOverrides.expFeatures;
    }
}

/**
 * Makes the Sapphire API calls to get the user auth information for Android SA.
 */
async function initUserConfigForSuperAppAndroid(): Promise<void> {
    userConfig = {};

    const locationHref = window.location.href;

    const sapphireId = getQueryParameterByName("muid", locationHref);
    userConfig.sapphireId = (sapphireId !== null) ? sapphireId : "";
    userConfig.muid = (sapphireId !== null) ? `m-${sapphireId}` : "";

    const adid = getQueryParameterByName("adid", locationHref);
    userConfig.adid = (adid !== null) ? adid : "";

    const market = getQueryParameterByName("market", locationHref);
    userConfig.market = getValidMarket(market);

    setMarketInfo(userConfig);

    const features = getQueryParameterByName("features", locationHref);
    userConfig.features = (features !== null) ? features : "";

    const activityId = getQueryParameterByName("activityId", locationHref);
    userConfig.activityId = (activityId !== null) ? activityId : "";

    const platform = getQueryParameterByName("platform", locationHref);
    userConfig.platform = (platform !== null) ? DevicePlatform[platform] : "";

    userConfig.bridgeVersionInt = 1;
    const bridgeVersionInt = getQueryParameterByName("bridgeVersionInt", locationHref);
    if (bridgeVersionInt) {
        userConfig.bridgeVersionInt = parseInt(bridgeVersionInt, 10);
    }

    const isDarkMode = getQueryParameterByName("isDarkMode", locationHref);
    userConfig.isDarkMode = (isDarkMode === "true");

    const isSignedIn = getQueryParameterByName("isSignedIn", locationHref);
    userConfig.isSignedIn = (isSignedIn === "true");

    const accountType = getQueryParameterByName("accountType", locationHref);
    userConfig.accountType = (accountType !== null) ? accountType : "";

    const isPageRefresh = getQueryParameterByName("isPageRefresh", locationHref);
    userConfig.isPageRefresh = (isPageRefresh !== null) ? isPageRefresh === "1" : false;

    const isChinaBuild =  getQueryParameterByName("isChinaBuild", locationHref);
    userConfig.isChinaBuild = (isChinaBuild === "true");

    // Initialize the userInformation object
    userInformation = { isSignedIn: userConfig.isSignedIn, accountType: userConfig.accountType };

    // Font size, anid, latitue, and longitude were added to query params in a later super app change.
    // Use them when present, otherwise fallback to Bridge API.
    const fontSize = getQueryParameterByName("fontSize", locationHref);
    if (fontSize !== null) {
        userConfig.fontSize = parseInt(fontSize, 10);

        const anid = getQueryParameterByName("anid", locationHref);
        userConfig.anid = (anid !== null) ? anid : "";

        const latitude = getQueryParameterByName("latitude", locationHref);
        userConfig.latitude = (latitude !== null) ? latitude : "";

        const longitude = getQueryParameterByName("longitude", locationHref);
        userConfig.longitude = (longitude !== null) ? longitude : "";

        // Initialize the locationInformation object
        locationInformation = {
            lat: latitude,
            lng: longitude
        };

    } else {
        window.performance.mark("HP-initUserConfigForSuperAppAndroid-DeviceInformation-Start");
        getDeviceInformation().then((deviceInfo) => {
            console.log("getDeviceInfomation Bridge call complete");
            window.performance.mark("HP-initUserConfigForSuperAppAndroid-DeviceInformation-End");
            window.performance.measure("HP-initUserConfigForSuperAppAndroid-DeviceInformation", "HP-initUserConfigForSuperAppAndroid-DeviceInformation-Start", "HP-initUserConfigForSuperAppAndroid-DeviceInformation-End");

            userConfig.anid = deviceInfo.ANID;
            userConfig.fontSize = deviceInfo.fontSize;
        });

        window.performance.mark("HP-initUserConfigForSuperAppAndroid-LocationInformation-Start");
        getLocationInfoAsync({}).then((info) => {
            console.log("getLocationInfo Bridge call complete");
            window.performance.mark("HP-initUserConfigForSuperAppAndroid-LocationInformation-End");
            window.performance.measure("HP-initUserConfigForSuperAppAndroid-LocationInformation", "HP-initUserConfigForSuperAppAndroid-LocationInformation-Start", "HP-initUserConfigForSuperAppAndroid-LocationInformation-End");

            try {
                // Initialize the locationInformation object
                locationInformation = JSON.parse(info);

                if (locationInformation) {
                    userConfig.latitude = locationInformation.lat;
                    userConfig.longitude = locationInformation.lng;
                }
            } catch (exception) {
                logErrorMessage(exception, "SapphireUserConfig.initUserConfigForSuperAppAndroid: Failed to parse location response");
            }
        });
    }
}

/**
 * Returns user configuration.
 */
export function getUserConfig(): UserConfig {
    return userConfig;
}

/**
 * Returns MSA or AAD access token.
 * @param scope - The scope
 */
export async function getAccessToken(scope: string): Promise<string> {
    if (userInformation.isSignedIn && userInformation.accountType === "AAD") {
        const result = await getUserInfoAsync({
            refresh: true,
            accountType: "AAD"
        });

        try {
            const accessTokenInfo = JSON.parse(result);

            if (accessTokenInfo.accessToken && accessTokenInfo.accessToken.length > 0) {
                return `Bearer ${accessTokenInfo.accessToken}`;
            }
        } catch (exception) {
            logErrorMessage(exception, "SapphireUserConfig.getAccessToken: Failed to parse AAD access token");
        }
    }

    if (userInformation.isSignedIn && userInformation.accountType === "MSA") {
        const result = await getUserInfoAsync({
            refresh: true,
            accountType: "MSA",
            type: "AccessToken",
            scope: scope
        });

        try {
            const accessTokenInfo = JSON.parse(result);

            if (accessTokenInfo.succeed) {
                return accessTokenInfo.accessToken;
            }
        } catch (exception) {
            logErrorMessage(exception, "SapphireUserConfig.getAccessToken: Failed to parse MSA access token");
        }
    }

    return null;
}

/**
 * Makes Sapphire API call to get the device information and return data.
 */
export async function getDeviceInformation(): Promise<DeviceInformation> {
    const info = await getDeviceInfoAsync({});

    try {
        return JSON.parse(info) as DeviceInformation;
    } catch (exception) {
        logErrorMessage(exception, "SapphireUserConfig.getDeviceInformation: Failed to parse response");
    }
}

/**
 * Makes Sapphire API call to get user information.
 */
async function getUserInformationInternal(): Promise<void> {
    const info = await getUserInfoAsync({});

    try {
        userInformation = JSON.parse(info) as UserInformation;
    } catch (exception) {
        logErrorMessage(exception, "SapphireUserConfig.getUserInformationInternal: Failed to parse response");
    }
}

/**
 * Makes Sapphire API call to get location information.
 */
async function getLocationInformationInternal(): Promise<void> {
    const info = await getLocationInfoAsync({});

    try {
        locationInformation = JSON.parse(info);
    } catch (exception) {
        logErrorMessage(exception, "SapphireUserConfig.getLocationInformationInternal: Failed to parse response");
    }
}

/**
 * log error message
 * @param error : error
 * @param customMessage : custom error message
 */
function logErrorMessage(error: Error, customMessage: string): void {
    logErrorAsync({
        type: "Failure",
        name: "HP_WEBVIEW_APP_ERROR",
        error: customMessage,
        data: {
            error: error,
            customMessage: customMessage
        }
    });
}

/**
 * Get valid market
 * @param deviceInforMarket : selected market
 */
function getValidMarket(deviceInforMarket: string): string {
    if (!deviceInforMarket) {
        return "en-us";
    }

    const selectedMarket = deviceInforMarket.toLowerCase();
    // Since OneService does not support en-id , fallback to en-xl
    return selectedMarket === "en-id" ? "en-xl" : selectedMarket;
}

/**
 * Set Market Info
 * @param userConfig : UserConfig
 */
function setMarketInfo(userConfig: UserConfig) {
    const data = userConfig.market.split("-");

    if (data && data.length == 2) {
        userConfig.language = data[0];
        userConfig.countryCode = data[1];
    }
}

/**
 * Utility method to extract query string param in url.
 *
 * @param name - Query param name
 * @param href - URL
 */
export function getQueryParameterByName(name: string, href: string): string {
    if (!hrefParamsObj[href]) {
        hrefParamsObj[href] = getParamsObj(href);
    }

    const paramsObj = hrefParamsObj[href];
    const value = paramsObj[name];

    return (value !== undefined) ? value : null;
}

/**
 * Helper method to parse the given URLs query parameters to a lookup dictionary
 *
 * @param url - The URL string with the query params to parse
 */
function getParamsObj(url: string): any {
    const paramsObj = {};
    const paramArray = getParamsFromUrl(url, true);

    if (paramArray && paramArray.length) {
        paramArray.forEach(keyValuePair =>
            paramsObj[keyValuePair.key] = keyValuePair.value);
    }

    return paramsObj;
}

/**
 * Parse parameters from the given URL
 * @param url - The URL to parse for parameters
 * @param decodeComponents - Flag to indicate whether the query param key and value should be URL decoded
 */
function getParamsFromUrl(url: string, decodeComponents = false): any {
    if (!url) {
        return null;
    }

    const queryString: string = (url || "").split("?")[1];
    return getParams(queryString, decodeComponents);
}

/**
 * Parse parameters from the given query string
 *
 * @param queryString - The query string to parse for parameters
 * @param decodeComponents - Flag to indicate whether the query param key and value should be URL decoded
 */
export function getParams(queryString: string, decodeComponents = false): any {
    const params: any = [];

    // Remove the leading ? if it is present
    if (queryString && queryString.startsWith("?")) {
        queryString = queryString.substring(1);
    }

    if (queryString) {
        // remove hash part.
        queryString = queryString.split("#")[0];
        const queryArray = queryString.split("&");

        for (let ndx = 0; ndx < queryArray.length; ndx++) {
            const parts = queryArray[ndx].split("=");
            let queryKey = parts[0];
            let queryValue = parts[1];

            if (decodeComponents) {
                queryKey = decodeURIComponentWithPlusSign(queryKey);
                if (queryValue) {
                    queryValue = decodeURIComponentWithPlusSign(queryValue);
                }
            }

            // Don't allow for duplicate query string parameter keys. Take the last one
            const newParam: any = { key: queryKey, value: queryValue };

            // @ts-ignore
            const existingParam = findIndex(params, (entry) => entry.key === newParam.key);
            if (existingParam === -1) {
                params.push(newParam);
            } else {
                params[existingParam] = newParam;
            }
        }
    }
    return params;
}

/**
 * Decode the given URL encoded string that may contain plus signs
 *
 * @param val - The value to decode.
 */
function decodeURIComponentWithPlusSign(val: string): string {
    val = val.replace(/\+/g, " ");
    val = decodeURIComponent(val);
    return val;
}